import React from "react";
import {
  FaMicrophone,
  FaPenFancy,
  FaExpand,
  FaBroadcastTower,
  FaFileAlt,
  FaLanguage,
} from "react-icons/fa";

/**
 * FeaturesPage.jsx
 * Single-file component (JSX + CSS) that shows:
 *  - fixed topbar + fixed header (logo + nav)
 *  - features grid (3x3) with the orange slide-up hover overlay
 *
 * Usage:
 *  import FeaturesPage from "./pages/FeaturesPage";
 *  <FeaturesPage />
 */

const features = [
  { icon: <FaMicrophone />, title: "Multiple Languages", text: "Provident nihil minus qui consequatur non omnis maiores. Eos accusantium minus dolores iure perferendis tempore et consectetur." },
  { icon: <FaBroadcastTower />, title: "Unlimited Documents", text: "Ut autem aut autem non a. Sint sint sit facilis nam justo sint. Libero corrupti neque eum hic non ut nesciunt dolorem." },
  { icon: <FaExpand />, title: "Accessibility", text: "Ut excepturi voluptatem nisi sed. Quidem fuga consequatur. Minus ea aut. Vel qui id voluptas adipisci eos earum corrupti." },
  { icon: <FaFileAlt />, title: "Individual Dashboard", text: "Non et temporibus minus omnis sed dolor esse consequatur. Cupiditate sed error ea fuga sit provident adipisci neque." },
  { icon: <FaMicrophone />, title: "Dashboard", text: "Cumque et suscipit saepe. Est maiores autem enim facilis ut aut ipsam corporis aut. Sed animi at autem alias eius labore." },
  { icon: <FaLanguage />, title: "Documents Classification", text: "Hic molestias ea quibusdam eos. Fugiat enim doloremque aut neque non et debitis iure. Corrupti recusandae ducimus enim." },
  { icon: <FaPenFancy />, title: "Corporate Packages", text: "Lorem ipsum dolor sit amet, consectetur adipisicing elit. Architecto, nemo." },
  { icon: <FaFileAlt />, title: "Voice Recordings to Text", text: "Hic molestias ea quibusdam eos. Fugiat enim doloremque aut neque non et debitis iure." },
  { icon: <FaLanguage />, title: "AI-powered Transcription", text: "Fast and accurate transcriptions in many languages with smart corrections." },
];

export default function FeaturesPage() {
  return (
    <>
      {/* Topbar (fixed) */}
      
      {/* Header (fixed below topbar) */}
     
      {/* Page content wrapper - pushes content below fixed bars */}
      <div className="page-wrap">
        <main className="container">
          <section className="features-section">
            <h2 className="section-title">Our Features</h2>

            <div className="features-grid">
              {features.map((f, idx) => (
                <article className="feature-card" key={idx}>
                  <div className="feature-inner">
                    <div className="feature-icon" aria-hidden>{f.icon}</div>
                    <h3 className="feature-title">{f.title}</h3>
                    <p className="feature-text">{f.text}</p>
                  </div>
                </article>
              ))}
            </div>
          </section>
        </main>
      </div>

      {/* Page footer */}
      

      {/* ===== component CSS (paste into your global CSS if you prefer) ===== */}
      <style>{`
:root {
  --fp-topbar-h: 40px;
  --fp-header-h: 84px; /* adjust if your header height differs */
  --fp-header-h-mobile: 64px;
  --fp-container: 1180px;
}

/* container helper */
.container {
  width: min(var(--fp-container), 94%);
  margin: 0 auto;
}

/* ---------- Topbar (fixed) ---------- */
.fp-topbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: var(--fp-topbar-h);
  background: #2e2e2e;
  color: #fff;
  z-index: 1500;
}
.fp-topbar-inner {
  display:flex; justify-content:space-between; align-items:center; height:100%;
  padding: 0 6%;
  font-size: 14px;
}
.fp-social { margin-right:10px; opacity:0.95; }

/* ---------- Header (fixed below topbar) ---------- */
.fp-header {
  position: fixed;
  top: var(--fp-topbar-h);
  left: 0;
  right: 0;
  height: var(--fp-header-h);
  background: #fff;
  z-index: 1400;
  border-bottom: 1px solid #eee;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}
.header-inner { display:flex; align-items:center; justify-content:space-between; height:100%; padding: 0 6%; }

.fp-logo { height: 56px; object-fit:contain; }

/* nav */
.nav-list { list-style:none; display:flex; gap:20px; margin:0; padding:0; align-items:center; }
.nav-list a { text-decoration:none; color:#333; font-weight:500; }
.nav-list a:hover { color:#e96b56; }

/* ---------- Page wrapper: push content below fixed topbar+header ---------- */
.page-wrap {
  /* important: sum of topbar + header to avoid overlap */
  padding-top: 0px;
  padding-bottom: 60px;
}

/* ---------- Features section ---------- */
.features-section { padding: 10px 0 40px; }
.section-title { font-size: 26px; margin: 12px 0 22px; color:#333; }

/* grid: 3 columns desktop */
.features-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 28px;
}

/* card base */
.feature-card {
  background: #fff;
  border-radius: 6px;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  min-height: 260px;
  transition: transform .35s ease, box-shadow .35s ease;
  box-shadow: 0 6px 18px rgba(0,0,0,0.05);
}

/* inner content padding */
.feature-inner {
  position: relative;
  z-index: 3;
  padding: 36px 28px;
  display:flex;
  flex-direction:column;
}

/* icon */
.feature-icon { font-size:26px; color:#e86f22; width:56px; height:56px; display:inline-flex; align-items:center; justify-content:center; margin-bottom:18px; }

/* title */
.feature-title { font-size:18px; color:#0b64ff; margin:0 0 10px; text-decoration: underline; text-underline-offset:6px; }

/* text */
.feature-text { color:#666; line-height:1.6; margin-top:auto; }

/* overlay slide-up orange on hover */
.feature-card::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg,#e96b56 0%, #e96b56 100%);
  z-index: 2;
  transform: translateY(100%);
  transition: transform .45s cubic-bezier(.2,.9,.2,1);
}
.feature-card:hover::before { transform: translateY(0%); }

/* hover elevates card and changes text/icons to white */
.feature-card:hover { transform: translateY(-6px); box-shadow: 0 20px 40px rgba(0,0,0,0.12); }
.feature-card:hover .feature-title,
.feature-card:hover .feature-text,
.feature-card:hover .feature-icon { color:#fff !important; }

/* responsive: tablet -> 2 columns */
@media (max-width: 1000px) {
  .features-grid { grid-template-columns: repeat(2, 1fr); }
  .page-wrap { padding-top: 0px }
}

/* mobile: 1 column */
@media (max-width: 600px) {
  .features-grid { grid-template-columns: 1fr; gap: 18px; }
  :root { --fp-header-h: var(--fp-header-h-mobile); }
  .page-wrap { padding-top: 0px}
  .fp-logo { height:44px; }
  .feature-inner { padding: 22px; }
  .feature-card { min-height: 180px; }
}
      `}</style>
    </>
  )

}
